import { frameTimecode } from '../../domain/frame-timecode';

export const FPS = 30;
export interface TimeWindow { start: number; span: number }
export const clamp = (value: number, low: number, high: number) => Math.max(low, Math.min(high, value));
export function timeWindow(start: number, span: number, last: number): TimeWindow {
  const bounded = clamp(span, Math.min(29, last), last);
  return { start: clamp(start, 0, last - bounded), span: bounded };
}
export const frameX = (frame: number, view: TimeWindow, width: number) => (frame - view.start) / view.span * width;
export const xFrame = (x: number, view: TimeWindow, width: number, last: number) =>
  Math.round(clamp(view.start + x / Math.max(1, width) * view.span, 0, last));
export function zoomWindow(view: TimeWindow, factor: number, anchor: number, fraction: number, last: number) {
  const span = clamp(view.span / factor, Math.min(29, last), last);
  return timeWindow(anchor - span * fraction, span, last);
}
export function fitRange(first: number, end: number, last: number) {
  const span = Math.max(29, end - first);
  return timeWindow(Math.floor(first - span * .1), Math.ceil(span * 1.2), last);
}
export function rulerTicks(view: TimeWindow, width: number, last: number) {
  const intervals = [1, 2, 5, 10, 15, 30, 60, 150, 300, 450, 900, 1800, 4500, 9000, 18000];
  const spacing = Math.max(width < 700 ? 64 : 80, view.span / width < 30 / 80 ? 100 : 0);
  const major = intervals.find(step => step / view.span * width >= spacing) ?? 18000;
  const minor = major % 5 === 0 ? major / 5 : major % 2 === 0 ? major / 2 : major;
  const ticks: { frame: number; label: string | null }[] = [];
  for (let frame = Math.ceil(view.start / minor) * minor; frame <= Math.min(last, view.start + view.span); frame += minor) {
    const label = frame % major ? null : major < FPS ? frameTimecode(frame)
      : last >= 1799 ? `${Math.floor(frame / 1800)}:${String(Math.floor(frame / FPS) % 60).padStart(2, '0')}` : `${frame / FPS}s`;
    ticks.push({ frame, label: frameX(frame, view, width) > width - (major < FPS ? 100 : 40) ? null : label });
  }
  return ticks;
}
export function outputPosition(frame: number, range: { inFrame: number; outFrame: number }, outputFrames: number) {
  if (frame < range.inFrame || frame > range.outFrame) return null;
  return Math.round((frame - range.inFrame) / Math.max(1, range.outFrame - range.inFrame) * (outputFrames - 1));
}

export interface MarkerTime { id: string; number: number; frame: number }
export function markerLayout(markers: MarkerTime[], view: TimeWindow, width: number) {
  const groups: { items: MarkerTime[]; x: number; lane: number }[] = [];
  const ends = [-Infinity, -Infinity];
  for (const item of markers.filter(item => item.frame >= view.start && item.frame <= view.start + view.span).sort((a, b) => a.frame - b.frame)) {
    const x = frameX(item.frame, view, width);
    const lane = ends.findIndex(end => x - end >= 44);
    if (lane >= 0) { ends[lane] = x; groups.push({ items: [item], x, lane }); }
    else { const group = groups.at(-1)!; group.items.push(item); ends[group.lane] = x; }
  }
  return groups;
}
